import { BankOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import React, { useState } from 'react';
import { LoanData, formatDate, formatInr } from './mockData';
import styles from './styles.module.css';

type Props = {
  loan: LoanData;
  financier?: string;
  onLinked: () => void;
};

const titleCase = (s: string) =>
  s
    .toLowerCase()
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const LoanTracker: React.FC<Props> = ({ loan, financier, onLinked }) => {
  const [linking, setLinking] = useState(false);

  if (!loan.linked) {
    return (
      <div className={styles.loanTracker}>
        <div className={styles.loanLenderRow}>
          <div className={styles.loanLender}>
            <BankOutlined style={{ color: '#0460ff' }} />
            {financier ? titleCase(financier) : 'Vehicle financier'}
          </div>
          <span className={styles.loanBadge}>via AA</span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Connect your loan account through Account Aggregator to see
          outstanding balance, EMI schedule and get repayment reminders.
        </div>
        <Button
          className={styles.loanCtaPrimary}
          loading={linking}
          onClick={() => {
            setLinking(true);
            setTimeout(() => {
              message.success('Loan linked via Account Aggregator');
              setLinking(false);
              onLinked();
            }, 1200);
          }}
        >
          Link via Account Aggregator
        </Button>
      </div>
    );
  }

  const paid =
    (loan.sanctionedAmount ?? 0) - (loan.outstandingAmount ?? 0);
  const progress = loan.sanctionedAmount
    ? Math.round((paid / loan.sanctionedAmount) * 100)
    : 0;

  return (
    <div className={styles.loanTracker}>
      <div className={styles.loanLenderRow}>
        <div className={styles.loanLender}>
          <BankOutlined style={{ color: '#0460ff' }} />
          {titleCase(loan.lender ?? '')}
        </div>
        <span className={styles.loanBadge}>
          A/c {loan.accountNumber}
        </span>
      </div>

      <div>
        <div className={styles.loanNumberRow}>
          <span>Paid {formatInr(paid)}</span>
          <span>{progress}%</span>
        </div>
        <div className={styles.loanProgress}>
          <div
            className={styles.loanProgressFill}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className={styles.loanNumberRow} style={{ marginTop: 4 }}>
          <span>Outstanding</span>
          <strong style={{ color: '#111' }}>
            {formatInr(loan.outstandingAmount ?? 0)}
          </strong>
        </div>
      </div>

      <div className={styles.loanGrid}>
        <div className={styles.loanBox}>
          <div className={styles.loanBoxLabel}>Next EMI</div>
          <div className={styles.loanBoxValue}>
            {formatInr(loan.emiAmount ?? 0)}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280' }}>
            on {formatDate(loan.nextEmiDate ?? '')}
          </div>
        </div>
        <div className={styles.loanBox}>
          <div className={styles.loanBoxLabel}>Tenure left</div>
          <div className={styles.loanBoxValue}>
            {loan.tenureRemaining} mo
          </div>
          <div style={{ fontSize: 10, color: '#6b7280' }}>
            @ {loan.interestRate}% p.a.
          </div>
        </div>
      </div>

      <div className={styles.loanCtaRow}>
        <Button
          className={styles.loanCtaPrimary}
          onClick={() => message.success('EMI auto-debit scheduled')}
        >
          Set EMI auto-debit
        </Button>
        <Button
          className={styles.loanCtaSecondary}
          onClick={() =>
            message.info(
              'Refinance offer: 8.6% with Good Bank — saves ₹2,140/mo'
            )
          }
        >
          Refinance
        </Button>
      </div>
    </div>
  );
};

export default LoanTracker;
